import React, { useEffect, useMemo, useState } from 'react';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import Icon from '@mui/material/Icon';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import { alpha, useTheme as useMuiTheme } from '@mui/material/styles';

import { SoftCard } from 'shared/ui/soft/SoftCard';
import { TabPanel } from 'shared/ui/soft/TabPanel';
import { PillIcon } from 'shared/ui/soft/PillIcon';

import { attendanceApi } from 'sectors/pilates/attendance/api/attendanceApi';

function hmFromMin(min) {
  const v = Number(min);
  if (!Number.isFinite(v)) return '--:--';
  const hh = String(Math.floor(v / 60)).padStart(2, '0');
  const mm = String(v % 60).padStart(2, '0');
  return `${hh}:${mm}`;
}

function safeDate(value) {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function sessionDateKey(row) {
  return `${row?.dayISO || ''}T${hmFromMin(row?.startMin)}`;
}

function markedAtKey(row) {
  return row?.createdAt || sessionDateKey(row);
}

function formatDateTR(dayISO) {
  if (!dayISO) return '--';
  const date = safeDate(`${dayISO}T00:00:00`);
  return date ? date.toLocaleDateString('tr-TR', { day: '2-digit', month: 'short' }) : dayISO;
}

function formatDateTimeTR(value) {
  const date = safeDate(value);
  return date
    ? date.toLocaleString('tr-TR', {
        day: '2-digit',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '--';
}

function formatPlanRange(startAt, endAt) {
  const start = safeDate(startAt);
  const end = safeDate(endAt);
  if (!start && !end) return 'Tarih bilgisi yok';
  const fmt = (value) =>
    value.toLocaleDateString('tr-TR', { day: '2-digit', month: 'short', year: 'numeric' });
  if (start && end) return `${fmt(start)} - ${fmt(end)}`;
  return start ? `${fmt(start)} itibariyla` : `Tahmini bitis ${fmt(end)}`;
}

function packageTitleOf(pkg) {
  return String(pkg?.planName || pkg?.name || pkg?.packageName || '').trim();
}

function normalizePackageCatalog(memberPackages = []) {
  return (Array.isArray(memberPackages) ? memberPackages : [])
    .map((pkg, index) => {
      const title = packageTitleOf(pkg);
      const startAt =
        pkg?.planStartAt ||
        pkg?.memberPlanStartAt ||
        pkg?.startAt ||
        pkg?.plannedStartAt ||
        pkg?.soldAt ||
        null;
      const endAt =
        pkg?.planEstimatedEndAt || pkg?.planEndAt || pkg?.endAt || pkg?.packageEndAt || null;
      const createdAt = pkg?.createdAt || pkg?.soldAt || startAt || null;
      const total =
        Number(
          pkg?.packageTotal ??
            pkg?.packageTotalSessions ??
            pkg?.totalSessions ??
            pkg?.items?.[0]?.totalSessions
        ) || 0;

      return {
        key: String(
          pkg?.id ?? pkg?._id ?? pkg?.memberPlanId ?? pkg?.memberPackageId ?? `pkg-${index}`
        ),
        title,
        memberPlanId: pkg?.memberPlanId ?? pkg?.id ?? pkg?._id ?? pkg?.memberPackageId ?? null,
        packagePeriod: String(pkg?.packagePeriod || pkg?.planPeriod || '').trim(),
        startAt,
        endAt,
        createdAt,
        total,
      };
    })
    .filter((pkg) => pkg.title)
    .sort(
      (a, b) => (safeDate(b.createdAt)?.getTime() || 0) - (safeDate(a.createdAt)?.getTime() || 0)
    );
}

function annotateRowsWithInferredPackageSequence(rows = []) {
  const sorted = (Array.isArray(rows) ? rows : [])
    .slice()
    .sort(
      (a, b) =>
        (safeDate(sessionDateKey(a))?.getTime() || 0) -
        (safeDate(sessionDateKey(b))?.getTime() || 0)
    );

  const states = new Map();
  const bucketMaxRemaining = new Map();

  sorted.forEach((row) => {
    const planName = String(row?.planName || '').trim() || 'Pakete baglanmamis';
    const hasStrongContext = Boolean(
      row?.memberPlanId ||
        row?.memberPackageId ||
        row?.packageId ||
        row?.planStartAt ||
        row?.planEstimatedEndAt ||
        row?.packagePeriod ||
        row?.planPeriod
    );

    if (hasStrongContext) return;

    const currentRemaining = Number(row?.remainingNormalAfter);
    if (!Number.isFinite(currentRemaining)) return;

    const prev = states.get(planName) || { bucket: 0, lastRemaining: null };
    let nextBucket = prev.bucket;

    // Remaining count should normally go down within the same package.
    // If it jumps up (0 -> 7, 1 -> 6, etc.), that strongly suggests a new package started.
    if (prev.lastRemaining != null && currentRemaining > prev.lastRemaining) {
      nextBucket += 1;
    }

    row.__inferredPackageSeq = nextBucket;

    const bucketKey = `${planName}::${nextBucket}`;
    bucketMaxRemaining.set(
      bucketKey,
      Math.max(bucketMaxRemaining.get(bucketKey) ?? -1, currentRemaining)
    );

    states.set(planName, {
      bucket: nextBucket,
      lastRemaining: currentRemaining,
    });
  });

  return (Array.isArray(rows) ? rows : []).map((row) => {
    const planName = String(row?.planName || '').trim() || 'Pakete baglanmamis';
    const bucket = row.__inferredPackageSeq;
    const inferredTotal =
      bucket == null
        ? null
        : (bucketMaxRemaining.get(`${planName}::${bucket}`) ?? null) != null
        ? (bucketMaxRemaining.get(`${planName}::${bucket}`) ?? 0) + 1
        : null;

    return {
      ...row,
      inferredPackageSeq: bucket ?? null,
      inferredPackageTotal: inferredTotal,
    };
  });
}

function resolveRowPackageContext(row, packageCatalog = []) {
  const planName = String(row?.planName || '').trim();
  if (!planName) return row;

  const rowPlanId = row?.memberPlanId ?? row?.memberPackageId ?? row?.packageId ?? null;
  const sessionAt = safeDate(sessionDateKey(row)) || safeDate(`${row?.dayISO || ''}T00:00:00`);

  let match = null;

  if (rowPlanId != null) {
    match =
      packageCatalog.find((pkg) => String(pkg.memberPlanId) === String(rowPlanId)) ||
      packageCatalog.find((pkg) => String(pkg.key) === String(rowPlanId)) ||
      null;
  }

  if (!match) {
    const sameName = packageCatalog.filter((pkg) => pkg.title === planName);

    if (sameName.length === 1) {
      match = sameName[0];
    } else if (sameName.length > 1 && sessionAt) {
      const sessionTs = sessionAt.getTime();

      match =
        sameName.find((pkg) => {
          const startTs = safeDate(pkg.startAt)?.getTime() || null;
          const endTs = safeDate(pkg.endAt)?.getTime() || null;
          if (startTs != null && endTs != null) return sessionTs >= startTs && sessionTs <= endTs;
          if (startTs != null) return sessionTs >= startTs;
          return false;
        }) ||
        sameName
          .filter((pkg) => (safeDate(pkg.startAt)?.getTime() || 0) <= sessionTs)
          .sort(
            (a, b) => (safeDate(b.startAt)?.getTime() || 0) - (safeDate(a.startAt)?.getTime() || 0)
          )[0] ||
        sameName[0];
    } else if (sameName.length > 1) {
      match = sameName[0];
    }
  }

  if (!match) return row;

  return {
    ...row,
    memberPlanId: rowPlanId ?? match.memberPlanId ?? null,
    packagePeriod: String(
      row?.packagePeriod || row?.planPeriod || match.packagePeriod || ''
    ).trim(),
    planStartAt: row?.planStartAt || row?.packageStartAt || match.startAt || null,
    planEstimatedEndAt: row?.planEstimatedEndAt || row?.packageEndAt || match.endAt || null,
    packageTotal:
      row?.packageTotal ??
      row?.packageTotalSessions ??
      row?.inferredPackageTotal ??
      match.total ??
      null,
  };
}

function buildPackageGroupKey(row) {
  const planName = String(row?.planName || '').trim() || 'Pakete baglanmamis';
  const period = String(row?.packagePeriod || row?.planPeriod || '').trim();
  const startAt = row?.planStartAt || row?.packageStartAt || row?.planCreatedAt || '';
  const endAt = row?.planEstimatedEndAt || row?.packageEndAt || '';
  const total = row?.packageTotal ?? row?.packageTotalSessions ?? '';
  const memberPlanId = row?.memberPlanId ?? row?.memberPackageId ?? row?.packageId ?? '';
  const inferredSeq = row?.inferredPackageSeq ?? '';

  return `snapshot:${memberPlanId}::${planName}::${period}::${startAt}::${endAt}::${total}::${inferredSeq}`;
}

function statusMeta(status, theme, isDark) {
  const value = String(status || '').toUpperCase();

  if (value === 'ATTENDED') {
    return {
      key: 'ATTENDED',
      label: 'Katildi',
      icon: 'check_circle',
      bg: alpha(theme.palette.success.main, isDark ? 0.18 : 0.1),
      border: alpha(theme.palette.success.main, 0.24),
      text: theme.palette.success.main,
    };
  }

  if (value === 'NO_SHOW') {
    return {
      key: 'NO_SHOW',
      label: 'Gelmedi',
      icon: 'event_busy',
      bg: alpha(theme.palette.warning.main, isDark ? 0.18 : 0.1),
      border: alpha(theme.palette.warning.main, 0.24),
      text: theme.palette.warning.main,
    };
  }

  if (value === 'BURNED') {
    return {
      key: 'BURNED',
      label: 'Yandi',
      icon: 'local_fire_department',
      bg: alpha(theme.palette.error.main, isDark ? 0.18 : 0.1),
      border: alpha(theme.palette.error.main, 0.24),
      text: theme.palette.error.main,
    };
  }

  if (value === 'CANCELLED') {
    return {
      key: 'CANCELLED',
      label: 'Iptal',
      icon: 'cancel',
      bg: alpha(theme.palette.error.main, isDark ? 0.16 : 0.08),
      border: alpha(theme.palette.error.main, 0.2),
      text: theme.palette.error.main,
    };
  }

  if (value === 'PENDING') {
    return {
      key: 'PENDING',
      label: 'Bekliyor',
      icon: 'hourglass_top',
      bg: alpha(theme.palette.info.main, isDark ? 0.18 : 0.1),
      border: alpha(theme.palette.info.main, 0.24),
      text: theme.palette.info.main,
    };
  }

  return {
    key: value || 'UNKNOWN',
    label: value || 'Belirsiz',
    icon: 'help',
    bg: alpha(theme.palette.text.primary, isDark ? 0.1 : 0.05),
    border: alpha(theme.palette.text.primary, isDark ? 0.16 : 0.1),
    text: theme.palette.text.primary,
  };
}

function MetricCard({ label, value, tone = 'info' }) {
  const theme = useMuiTheme();
  const tones = {
    info: theme.palette.info.main,
    success: theme.palette.success.main,
    warning: theme.palette.warning.main,
    error: theme.palette.error.main,
  };
  const color = tones[tone] || theme.palette.info.main;

  return (
    <MDBox
      sx={{
        p: 1.2,
        borderRadius: '16px',
        border: `1px solid ${alpha(color, 0.16)}`,
        bgcolor: alpha(color, 0.06),
        minWidth: 0,
      }}
    >
      <MDTypography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700 }}>
        {label}
      </MDTypography>
      <MDTypography variant="h6" fontWeight="bold" sx={{ lineHeight: 1.1 }}>
        {value}
      </MDTypography>
    </MDBox>
  );
}

function StatPill({ icon, label, value, tone = 'info' }) {
  const theme = useMuiTheme();
  const palette =
    tone === 'success'
      ? theme.palette.success
      : tone === 'warning'
      ? theme.palette.warning
      : tone === 'error'
      ? theme.palette.error
      : theme.palette.info;

  return (
    <MDBox
      sx={{
        px: 0.85,
        py: 0.45,
        borderRadius: '999px',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.55,
        bgcolor: alpha(palette.main, 0.1),
        border: `1px solid ${alpha(palette.main, 0.18)}`,
      }}
    >
      <Icon sx={{ fontSize: 15, color: palette.main }}>{icon}</Icon>
      <MDTypography variant="caption" sx={{ color: palette.main, fontWeight: 800 }}>
        {label}: {value}
      </MDTypography>
    </MDBox>
  );
}

function AttendanceEventCard({ row, onOpenSession }) {
  const theme = useMuiTheme();
  const isDark = theme.palette.mode === 'dark';
  const meta = statusMeta(row?.attendanceStatus, theme, isDark);
  const note = String(row?.attendanceNote || '').trim();
  const clickable = Boolean(onOpenSession && row?.sessionId);
  const packageIndex =
    row?.usageIndex && row?.packageTotal
      ? `${row.usageIndex}/${row.packageTotal}`
      : row?.usageIndex
      ? `${row.usageIndex}`
      : '--';

  return (
    <MDBox
      onClick={clickable ? () => onOpenSession(row) : undefined}
      sx={{
        minWidth: 220,
        maxWidth: 220,
        p: 1,
        borderRadius: '16px',
        border: `1px solid ${meta.border}`,
        bgcolor: meta.bg,
        display: 'grid',
        gap: 0.45,
        flex: '0 0 auto',
        cursor: clickable ? 'pointer' : 'default',
        transition: 'transform .14s ease, box-shadow .14s ease',
        '&:hover': clickable
          ? {
              transform: 'translateY(-2px)',
              boxShadow: '0 12px 24px rgba(15,23,42,0.10)',
            }
          : undefined,
      }}
    >
      <MDBox display="flex" alignItems="center" justifyContent="space-between" gap={1}>
        <MDTypography variant="caption" fontWeight="bold">
          {formatDateTR(row?.dayISO)} - {hmFromMin(row?.startMin)}
        </MDTypography>
        <Icon sx={{ fontSize: 18, color: meta.text }}>{meta.icon}</Icon>
      </MDBox>

      <MDTypography variant="button" fontWeight="bold" sx={{ lineHeight: 1.15 }}>
        {row?.activityName || row?.packageItemActivityName || 'Seans'}
      </MDTypography>

      <MDTypography variant="caption" sx={{ color: 'text.secondary' }}>
        Durum: {meta.label}
      </MDTypography>
      <MDTypography variant="caption" sx={{ color: 'text.secondary' }}>
        Egitmen: {row?.trainerName || '--'}
      </MDTypography>
      <MDTypography variant="caption" sx={{ color: 'text.secondary' }}>
        Paket sirasi: {packageIndex}
      </MDTypography>
      <MDTypography variant="caption" sx={{ color: 'text.secondary' }}>
        Kalan normal: {row?.remainingNormalAfter ?? '--'} - Telafi:{' '}
        {row?.remainingMakeUpAfter ?? '--'}
      </MDTypography>

      <MDBox
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        gap={1}
        sx={{ mt: 0.25 }}
      >
        <MDTypography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700 }} noWrap>
          Isleyen: {row?.markedBy || '--'}
        </MDTypography>

        <MDBox display="flex" alignItems="center" gap={0.45}>
          {note ? (
            <Tooltip title={note} placement="top" arrow>
              <MDBox
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: '9px',
                  display: 'grid',
                  placeItems: 'center',
                  bgcolor: alpha(theme.palette.warning.main, 0.12),
                  border: `1px solid ${alpha(theme.palette.warning.main, 0.18)}`,
                }}
              >
                <Icon sx={{ fontSize: 15, color: theme.palette.warning.main }}>sticky_note_2</Icon>
              </MDBox>
            </Tooltip>
          ) : null}

          {clickable ? (
            <Tooltip title="Yoklama detayını aç" placement="top" arrow>
              <MDBox
                onClick={(event) => {
                  event.stopPropagation();
                  onOpenSession(row);
                }}
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: '9px',
                  display: 'grid',
                  placeItems: 'center',
                  bgcolor: alpha(theme.palette.info.main, 0.12),
                  border: `1px solid ${alpha(theme.palette.info.main, 0.18)}`,
                  cursor: 'pointer',
                }}
              >
                <Icon sx={{ fontSize: 15, color: theme.palette.info.main }}>open_in_new</Icon>
              </MDBox>
            </Tooltip>
          ) : null}
        </MDBox>
      </MDBox>
    </MDBox>
  );
}

function PackageFlowCard({ group, onOpenSession }) {
  const theme = useMuiTheme();
  const statusTotals = group.statusTotals || {};
  const lastItem = group.items[group.items.length - 1] || null;
  const progressValue =
    group.packageTotal && group.usedCount
      ? Math.max(4, Math.min(100, Math.round((group.usedCount / group.packageTotal) * 100)))
      : 0;

  return (
    <MDBox
      sx={{
        p: 1.2,
        borderRadius: '20px',
        border: `1px solid ${alpha(theme.palette.text.primary, 0.08)}`,
        bgcolor: alpha(theme.palette.background.paper, 0.98),
        display: 'grid',
        gap: 1.1,
      }}
    >
      <MDBox
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 280px) 1fr' },
          gap: 1,
          alignItems: 'start',
        }}
      >
        <MDBox sx={{ minWidth: 0, display: 'grid', gap: 0.75 }}>
          <MDBox display="flex" alignItems="center" justifyContent="space-between" gap={1}>
            <MDTypography fontWeight="bold" sx={{ lineHeight: 1.1 }} noWrap>
              {group.planName}
            </MDTypography>
            <Chip
              size="small"
              label={`${group.items.length} yoklama`}
              sx={{ borderRadius: '999px', fontWeight: 800 }}
            />
          </MDBox>

          <MDTypography variant="caption" sx={{ color: 'text.secondary' }}>
            {formatPlanRange(group.planStartAt, group.planEstimatedEndAt)}
          </MDTypography>

          {group.packagePeriod ? (
            <Chip
              size="small"
              label={group.packagePeriod}
              variant="outlined"
              sx={{
                width: 'fit-content',
                borderRadius: '999px',
                fontWeight: 800,
              }}
            />
          ) : null}

          <MDBox sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6 }}>
            <StatPill
              icon="fitness_center"
              label="Kullanim"
              value={group.progressLabel}
              tone="info"
            />
            <StatPill
              icon="check_circle"
              label="Katildi"
              value={statusTotals.ATTENDED || 0}
              tone="success"
            />
            {!!(statusTotals.NO_SHOW || statusTotals.BURNED || statusTotals.CANCELLED) && (
              <StatPill
                icon="warning_amber"
                label="Aksama"
                value={
                  (statusTotals.NO_SHOW || 0) +
                  (statusTotals.BURNED || 0) +
                  (statusTotals.CANCELLED || 0)
                }
                tone="warning"
              />
            )}
          </MDBox>

          {group.packageTotal ? (
            <MDBox sx={{ display: 'grid', gap: 0.35 }}>
              <MDBox
                sx={{
                  height: 7,
                  borderRadius: '999px',
                  bgcolor: alpha(theme.palette.info.main, 0.08),
                  overflow: 'hidden',
                }}
              >
                <MDBox
                  sx={{
                    width: `${progressValue}%`,
                    height: '100%',
                    borderRadius: '999px',
                    background: `linear-gradient(90deg, ${alpha(
                      theme.palette.info.main,
                      0.82
                    )}, ${alpha(theme.palette.success.main, 0.82)})`,
                  }}
                />
              </MDBox>
              <MDTypography variant="caption" sx={{ color: 'text.secondary' }}>
                Son islem: {lastItem ? formatDateTimeTR(markedAtKey(lastItem)) : '--'}
              </MDTypography>
            </MDBox>
          ) : null}
        </MDBox>

        <MDBox sx={{ minWidth: 0 }}>
          <MDBox
            sx={{
              display: 'flex',
              gap: 0.8,
              overflowX: 'auto',
              pb: 0.4,
              scrollSnapType: 'x proximity',
              '& > *': { scrollSnapAlign: 'start' },
            }}
          >
            {group.items.map((row) => (
              <AttendanceEventCard
                key={row?.attendanceId || `${row?.sessionId}-${dateKey(row)}`}
                row={row}
                onOpenSession={onOpenSession}
              />
            ))}
          </MDBox>
        </MDBox>
      </MDBox>
    </MDBox>
  );
}

export function AttendanceTab({ tab, memberId, memberPackages = [], limit = 120, onOpenSession }) {
  const theme = useMuiTheme();
  const isDark = theme.palette.mode === 'dark';

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let alive = true;
    const isActive = tab === 3;
    if (!isActive || !memberId) return undefined;

    (async () => {
      setLoading(true);
      setErr(null);
      try {
        const data = await attendanceApi.getMemberAttendanceHistory(memberId, limit);
        if (!alive) return;
        const normalized = Array.isArray(data) ? data.slice() : [];
        normalized.sort((a, b) => {
          const ta = safeDate(sessionDateKey(a))?.getTime() || 0;
          const tb = safeDate(sessionDateKey(b))?.getTime() || 0;
          return tb - ta;
        });
        setRows(normalized);
      } catch (error) {
        if (!alive) return;
        console.error('[AttendanceTab] getMemberAttendanceHistory failed', error);
        setRows([]);
        setErr('Yoklama gecmisi yuklenemedi.');
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [tab, memberId, limit]);

  const packageCatalog = useMemo(() => normalizePackageCatalog(memberPackages), [memberPackages]);

  const packageGroups = useMemo(() => {
    const source = annotateRowsWithInferredPackageSequence(
      (Array.isArray(rows) ? rows : []).map((row) => resolveRowPackageContext(row, packageCatalog))
    );
    const map = new Map();

    source.forEach((row) => {
      const rawPlanId = row?.memberPlanId;
      const planName = String(row?.planName || '').trim() || 'Pakete baglanmamis';
      const key = buildPackageGroupKey(row);

      if (!map.has(key)) {
        map.set(key, {
          key,
          memberPlanId: rawPlanId ?? null,
          planName,
          packagePeriod: String(row?.packagePeriod || row?.planPeriod || '').trim(),
          items: [],
          packageTotal: Number(row?.packageTotal) || 0,
          maxUsageIndex: 0,
          planStartAt: row?.planStartAt || null,
          planEstimatedEndAt: row?.planEstimatedEndAt || null,
          statusTotals: {},
        });
      }

      const group = map.get(key);
      group.items.push(row);
      group.packagePeriod =
        group.packagePeriod || String(row?.packagePeriod || row?.planPeriod || '').trim();
      group.packageTotal = Math.max(group.packageTotal, Number(row?.packageTotal) || 0);
      group.maxUsageIndex = Math.max(group.maxUsageIndex, Number(row?.usageIndex) || 0);
      group.planStartAt = group.planStartAt || row?.planStartAt || null;
      group.planEstimatedEndAt = group.planEstimatedEndAt || row?.planEstimatedEndAt || null;

      const metaKey = statusMeta(row?.attendanceStatus, theme, isDark).key;
      group.statusTotals[metaKey] = (group.statusTotals[metaKey] || 0) + 1;
    });

    return Array.from(map.values())
      .map((group) => {
        const sortedItems = group.items
          .slice()
          .sort(
            (a, b) =>
              (safeDate(sessionDateKey(a))?.getTime() || 0) -
              (safeDate(sessionDateKey(b))?.getTime() || 0)
          );
        const attendedCount = group.statusTotals.ATTENDED || 0;
        const usedCount = group.maxUsageIndex || attendedCount || sortedItems.length;

        return {
          ...group,
          items: sortedItems,
          usedCount,
          progressLabel: group.packageTotal ? `${usedCount}/${group.packageTotal}` : `${usedCount}`,
          latestAt: safeDate(sessionDateKey(sortedItems[sortedItems.length - 1]))?.getTime() || 0,
        };
      })
      .sort((a, b) => b.latestAt - a.latestAt);
  }, [rows, packageCatalog, theme, isDark]);

  const summary = useMemo(() => {
    const statusCounts = rows.reduce(
      (acc, row) => {
        const key = statusMeta(row?.attendanceStatus, theme, isDark).key;
        acc[key] = (acc[key] || 0) + 1;
        return acc;
      },
      { ATTENDED: 0, NO_SHOW: 0, BURNED: 0, CANCELLED: 0, PENDING: 0 }
    );

    return {
      total: rows.length,
      packageCount: packageGroups.length,
      attended: statusCounts.ATTENDED || 0,
      disrupted:
        (statusCounts.NO_SHOW || 0) + (statusCounts.BURNED || 0) + (statusCounts.CANCELLED || 0),
      pending: statusCounts.PENDING || 0,
    };
  }, [rows, packageGroups, theme, isDark]);

  return (
    <TabPanel value={tab} index={3}>
      <SoftCard>
        <MDBox display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 1.2 }}>
          <MDBox display="flex" alignItems="center" gap={1}>
            <PillIcon icon="fact_check" tone="teal" />
            <MDBox>
              <MDTypography variant="h6" fontWeight="bold" sx={{ lineHeight: 1.1 }}>
                Yoklama Akisi
              </MDTypography>
              <MDTypography variant="caption" sx={{ color: 'text.secondary' }}>
                Hangi pakette hangi seans kullanildi, kim isledi, durum neydi tek bakista gor.
              </MDTypography>
            </MDBox>
          </MDBox>

          <MDBox display="flex" alignItems="center" gap={0.8}>
            {loading ? <CircularProgress size={18} /> : null}
            <Chip
              size="small"
              label={`${summary.total} kayit`}
              variant="outlined"
              sx={{ borderRadius: '999px', fontWeight: 900 }}
            />
          </MDBox>
        </MDBox>

        {err ? (
          <MDTypography variant="caption" sx={{ color: 'error.main', fontWeight: 800, mb: 1 }}>
            {err}
          </MDTypography>
        ) : null}

        <MDBox
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr 1fr', lg: 'repeat(4, 1fr)' },
            gap: 0.9,
            mb: 1.15,
          }}
        >
          <MetricCard label="Paket" value={summary.packageCount} tone="info" />
          <MetricCard label="Katildi" value={summary.attended} tone="success" />
          <MetricCard label="Aksayan" value={summary.disrupted} tone="warning" />
          <MetricCard label="Bekleyen" value={summary.pending} tone="error" />
        </MDBox>

        {!loading && packageGroups.length === 0 ? (
          <MDBox
            sx={{
              p: 1.2,
              borderRadius: '16px',
              border: `1px solid ${alpha(theme.palette.text.primary, isDark ? 0.14 : 0.12)}`,
              bgcolor: isDark ? alpha('#fff', 0.03) : alpha('#fff', 0.92),
            }}
          >
            <MDTypography variant="button" sx={{ opacity: 0.75, fontWeight: 800 }}>
              Gosterilecek yoklama kaydi yok.
            </MDTypography>
          </MDBox>
        ) : null}

        <MDBox sx={{ display: 'grid', gap: 1 }}>
          {packageGroups.map((group, index) => (
            <React.Fragment key={group.key}>
              {index > 0 ? <Divider sx={{ opacity: 0.3 }} /> : null}
              <PackageFlowCard group={group} onOpenSession={onOpenSession} />
            </React.Fragment>
          ))}
        </MDBox>
      </SoftCard>
    </TabPanel>
  );
}
